import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useVsiAppContext } from "../stores/VsiApp";
import styles from "../utils/themeStyles";

import ThemedChartLight from "./ThemedChartLight";
import ThemedChartDark from "./ThemedChartDark";

import { genericColors } from "../utils/chartUtils";
import { ChartArea } from "../styled/Stockchart.styled";
import { withErrorBoundary } from "../boundaries/withErrorBoundary";
import GeneralErrorBoundary from "../boundaries/GeneralErrorBoundary";

function Stockchart({
                      categories,
                      data,
                      title,
                      series,
                      config,
                      height,
                      navDisabled,
                      yAxisTitle,
                      formatData,
                      formatLabels,
                      formatPoint,
                      mutable,
                      saveParams,
                      disableLegend,
                      animation = true,
                      isLoading,
                      rangeSelectorDisabled = false,
                      selectedDefaultZoom = 5
                    }) {
  const { theme, isTabletScreen, client, user: { reportMetadata }, updateReportMetadata } = useVsiAppContext();

  const chartComponent = useRef(null);

  const activeReportMetadata = useMemo(() => {
    let metaData = reportMetadata
      .filter(config => config.path === window.location.pathname && config.client === client._id);
    const keyedParams = Object.keys(saveParams || {});
    if (keyedParams.length > 0) {
      metaData = metaData?.filter(config => {
        return keyedParams.every(key => config?.params?.[key] === saveParams?.[key]);
      });
    }
    return metaData[0];
  }, [reportMetadata, saveParams, window.location.pathname]);

  const savedActiveRangeIndex = activeReportMetadata?.metadata?.activeRangeIndex;

  const [activeRangeIndex, setActiveRangeIndex] = useState(1);

  useEffect(() => {
    if (chartComponent.current && chartComponent.current.chart) {
      if (isLoading) {
        chartComponent.current.chart.showLoading("Loading...");
      } else {
        chartComponent.current.chart.hideLoading();
      }
    }
  }, [isLoading]);

  useEffect(() => {
    if (((data && data.length > 0) || (series && series.length > 0)) && activeRangeIndex === undefined) {
      if (savedActiveRangeIndex !== undefined && savedActiveRangeIndex !== null) {
        setActiveRangeIndex(savedActiveRangeIndex);
        // Update the chart if it's available
        if (chartComponent.current) {
          setChartOptions(options => ({
            ...options,
            rangeSelector: {
              ...options.rangeSelector,
              selected: savedActiveRangeIndex,
            }
          }));
        }
      }
    }
  }, [data, series, activeRangeIndex, savedActiveRangeIndex]);

  const basicTextStyle = useMemo(() => {
    return {
      color: styles[theme].textColor,
      fontFamily: styles[theme].fontFamily
    };
  }, [theme]);

  const updateSeries = (series, data) => {
    if (series) return [...series];
    else if (data)
      return [
        {
          data: [...data]
        }
      ];
    return [];
  };

  const handleYAxis = config => {
    const defaultYAxis = {
      title: {
        enabled: true,
        text: yAxisTitle,
        style: {
          ...basicTextStyle,
          fontSize: "115%",
          fontWeight: "600"
        }
      },
      opposite: false,
      labels: {
        style: {
          ...basicTextStyle,
          color: styles[theme].textColor
        },
        format: formatLabels
      }
    };
    if (!config) return {
      ...defaultYAxis
    };
    else if (typeof config?.yAxis === "object" && !Array.isArray(config?.yAxis)) {
      return {
        ...defaultYAxis,
        ...config.yAxis,
        title: {
          ...config.yAxis.title,
          style: defaultYAxis.title.style
        }
      };
    } else if (Array.isArray(config?.yAxis)) {
      return config.yAxis.map(axis => ({
        ...defaultYAxis,
        ...axis,
        title: {
          ...axis.title,
          style: defaultYAxis.title.style
        }
      }));
    }
  };
  // on save click
  const saveRangeSelectorZoom = useCallback(() => {
    const selectedIndex = chartComponent?.current?.chart?.rangeSelector?.selected;
    const doDelete = savedActiveRangeIndex === selectedIndex;
    updateReportMetadata({ activeRangeIndex: selectedIndex }, saveParams, doDelete);
    return false;
  }, [savedActiveRangeIndex]);

  // what is the series as soon as the component loads?
  const [intitialSeries] = useState(updateSeries(series, data));
  const [chartOptions, setChartOptions] = useState({
    colors: config?.colors ? [...config.colors] : [...genericColors],
    credits: { enabled: false },
    mapNavigation: { enabled: false },
    navigator: { enabled: !navDisabled },
    tooltip: {
      ...series?.tooltip,
      backgroundColor: styles[theme].altBgColor,
      style: basicTextStyle
    },
    plotOptions: {
      series: {
        dataLabels: {
          ...series?.dataLabels,
          enabled: true,
          color: basicTextStyle.color,
          format: formatData
        },
        animation: animation,
        ...config?.plotOptions?.series
      }
    },
    rangeSelector: {
      enabled: !rangeSelectorDisabled,
      buttons: [
        {
          type: "month",
          count: 1,
          text: "1m",
          title: "View 1 month",
          events: { click: () => handleRangeButtonClick(0) }
        },
        {
          type: "month",
          count: 3,
          text: "3m",
          title: "View 3 months",
          events: { click: () => handleRangeButtonClick(1) }
        },
        {
          type: "month",
          count: 6,
          text: "6m",
          title: "View 6 months",
          events: { click: () => handleRangeButtonClick(2) }
        },
        {
          type: "ytd",
          text: "YTD",
          title: "View year to date",
          events: { click: () => handleRangeButtonClick(3) }
        },
        {
          type: "year",
          count: 1,
          text: "1y",
          title: "View 1 year",
          events: { click: () => handleRangeButtonClick(4) }
        },
        {
          type: "all",
          text: "All",
          title: "View all",
          events: { click: () => handleRangeButtonClick(5) }
        },

        {
          type: "custom",
          text: "Save",
          title: "Save zoom",
          events: { click: () => saveRangeSelectorZoom() }
        }
      ],
      buttonTheme: {
        fill: styles[theme].sideBgColor,
        style: { color: styles[theme].textColor },
        states: {
          hover: { fill: styles[theme].navItemBgActive },
          select: {
            fill: styles[theme].navItemBgActive,
            style: { color: styles[theme].textColor }
          },
          disabled: {
            fill: styles[theme].disabledBtnColor,
            style: { cursor: "auto" }
          }
        }
      },
      inputStyle: { color: basicTextStyle.color },
      labelStyle: { color: basicTextStyle.color },
      selected: selectedDefaultZoom
    },
    xAxis: {
      categories: categories || undefined,
      type: "datetime",
      ordinal: false,
      labels: { style: basicTextStyle }
    },
    // Pass additional parameters to handleYAxis as needed.
    yAxis: handleYAxis({ ...config, theme, formatLabels }, yAxisTitle, basicTextStyle),
    chart: {
      type: "spline",
      backgroundColor: "transparent",
      color: styles[theme].textColor,
      height: height || 600,
      zoomType: "x",
      animation: animation,
      zooming: { mouseWheel: { enabled: false } }
    },
    title: {
      title: { style: basicTextStyle, text: title || "" },
      style: basicTextStyle
    },
    labels: { style: basicTextStyle },
    legend: {
      itemStyle: basicTextStyle,
      borderColor: "transparent",
      align: "center",
      enabled: !disableLegend
    },
    series: intitialSeries
  });

  // handler when a range button is clicked
  const handleRangeButtonClick = useCallback((newIndex) => setActiveRangeIndex(newIndex), [updateReportMetadata, setActiveRangeIndex, chartOptions]);
  // update chartOptions when dependencies change.
  // this ensures that when data arrives or when other props change,
  // the options are updated accordingly.
  useEffect(() => {

    const trueSeries = updateSeries(series, data);

    setChartOptions((prev) => ({
      ...prev,
      colors: config?.colors ? [...config.colors] : [...genericColors],
      yAxis: handleYAxis({ ...config, theme, formatLabels }, yAxisTitle, basicTextStyle),
      xAxis: {
        ...prev.xAxis,
        categories: categories || undefined,
        type: "datetime",
        labels: { style: basicTextStyle }
      },
      navigator: { enabled: !navDisabled },
      tooltip: {
        backgroundColor: styles[theme].altBgColor,
        style: basicTextStyle,
        pointFormat: formatPoint ? formatPoint : "{series.name}: <b>{point.y}</b>"
      },
      plotOptions: {
        ...prev.plotOptions,
        series: {
          dataLabels: {
            ...prev.plotOptions?.series?.dataLabels,
            color: basicTextStyle.color,
            format: formatData
          },
          animation: animation,
          ...config?.plotOptions?.series
        }
      },
      rangeSelector: {
        ...prev.rangeSelector,
        enabled: !rangeSelectorDisabled,
        buttonTheme: {
          fill: styles[theme].sideBgColor,
          style: { color: styles[theme].textColor },
          states: {
            hover: { fill: styles[theme].navItemBgActive },
            select: {
              fill: styles[theme].navItemBgActive,
              style: { color: styles[theme].textColor }
            },
            disabled: {
              fill: styles[theme].disabledBtnColor,
              style: { cursor: "auto" }
            }
          }
        },
        inputStyle: { color: basicTextStyle.color },
        labelStyle: { color: basicTextStyle.color }
      },
      chart: {
        ...prev.chart,
        height: height || 600,
        animation: animation
      },
      title: {
        ...prev.title,
        style: basicTextStyle,
        title: { ...prev.title.title, text: title || "" }
      },
      legend: {
        ...prev.legend,
        itemStyle: basicTextStyle,
        align: "center",
        enabled: !disableLegend
      },
      series: trueSeries
    }));
  }, [
    categories,
    series,
    data,
    theme,
    isTabletScreen,
    config?.yAxis,
    config?.colors,
    yAxisTitle,
    formatData,
    formatPoint,
    disableLegend,
    navDisabled,
    height,
    animation,
    formatLabels,
    config
  ]);


  // why this doesn't work is beyond me
  // my guess is the way the Highcharts component tree is set in memory

  return <ChartArea>
    {theme === "light" ? (
      <ThemedChartLight options={chartOptions}
                        chartComponent={chartComponent}
                        mutable={mutable}
                        constructorType={"stockChart"} />
    ) : (
      <ThemedChartDark options={chartOptions}
                       chartComponent={chartComponent}
                       mutable={mutable}
                       constructorType={"stockChart"} />
    )}
  </ChartArea>;
}

Stockchart.displayName = "Stockchart";

export default withErrorBoundary(Stockchart, GeneralErrorBoundary);
